import React, { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Button, Form, Card, Spinner, Container, Alert } from 'react-bootstrap';
import { createColSectionInstance } from '../../../Models/ColSection';
import { toast, ToastContainer } from 'react-toastify';
import { SketchPicker } from 'react-color';
import MediaSelector from '../Media/MediaSelector';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router-dom';
import { availableAnimations } from '../../../Services/Animations/availableAnimations';

const ColSectionBuilder = ({ id }) => {
    const [colSectionInstance] = useState(createColSectionInstance());
    const [loading, setLoading] = useState(false);
    const [defaultMessage, setDefaultMessage] = useState(false);
    
    // Content states
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [buttons, setButtons] = useState([]);
    const [direction, setDirection] = useState(colSectionInstance.getDirection());
    
    // Style states
    const [currentStyle, setCurrentStyle] = useState({});
    // Animation states
    const [leftAnimation, setLeftAnimation] = useState(colSectionInstance.getLeftAnimation());
    const [rightAnimation, setRightAnimation] = useState(colSectionInstance.getRightAnimation());
    const [showBgColorPicker, setShowBgColorPicker] = useState(false);
    const [showTitleColorPicker, setShowTitleColorPicker] = useState(false);
    const [showDescriptionColorPicker, setShowDescriptionColorPicker] = useState(false);
    
    // Media selector state
    const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
    
    // Button form states
    const [newButton, setNewButton] = useState({
        text: '',
        link: '',
        buttonType: 'primary'
    });

    // Validation states
    const [validated, setValidated] = useState(false);
    const [errors, setErrors] = useState({
        newButton: {
            text: false,
            link: false
        }
    });

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        try {
            setLoading(true);
            if (id) {
                var data = await colSectionInstance.load(id);
                if(Object.keys(data).length === 0){
                    setDefaultMessage(true);
                }
            } else {
                await colSectionInstance.load();
            }
                
            if(Object.keys(colSectionInstance.getData()).length > 0){
                setTitle(colSectionInstance.getTitle());
                setDescription(colSectionInstance.getDescription());
                setImageUrl(colSectionInstance.getImageUrl());
                setButtons(colSectionInstance.getButtons() || []);
                setCurrentStyle(colSectionInstance.getStyle());
                setLeftAnimation(colSectionInstance.getLeftAnimation());
                setRightAnimation(colSectionInstance.getRightAnimation());
                setDirection(colSectionInstance.getDirection());
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load column section data');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = (formData, type) => {
        const newErrors = { ...errors };
        let isValid = true;

        if (type === 'newButton') {
            newErrors.newButton = {
                text: !formData.text,
                link: !formData.link
            };
            isValid = !newErrors.newButton.text && !newErrors.newButton.link;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleMediaSelect = (selectedPath) => {
        if (selectedPath) {
            setImageUrl(selectedPath);
            colSectionInstance.setImageUrl(selectedPath);
        }
        setIsMediaSelectorOpen(false);
    };

    const handleBgColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            ColSection: {
                ...currentStyle.ColSection,
                backgroundColor: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        colSectionInstance.setStyle(updatedStyle);
    };

    const handleTitleColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            ColSectionTitle: {
                ...currentStyle.ColSectionTitle,
                color: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        colSectionInstance.setStyle(updatedStyle);
    };

    const handleDescriptionColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            ColSectionDescription: {
                ...currentStyle.ColSectionDescription,
                color: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        colSectionInstance.setStyle(updatedStyle);
    };

    const addButton = (event) => {
        if (event) {
            event.preventDefault();
        }
        
        if (!validateForm(newButton, 'newButton')) {
            return;
        }

        const button = {
            id: Date.now(),
            type: "button",
            button: {
                text: newButton.text,
                link: newButton.link,
                type: 'link',
                buttonType: newButton.buttonType,
                className: 'ColSectionBtn',
                style: {}
            }
        };

        const updatedButtons = [...buttons, button];
        setButtons(updatedButtons);
        colSectionInstance.setButtons(updatedButtons);
        
        setNewButton({
            text: '',
            link: '',
            buttonType: 'primary'
        });
        setErrors({
            ...errors,
            newButton: {
                text: false,
                link: false
            }
        });
    };

    const removeButton = (buttonId) => {
        const updatedButtons = buttons.filter(btn => btn.id !== buttonId);
        setButtons(updatedButtons);
        colSectionInstance.setButtons(updatedButtons);
    };

    const handleSaveChanges = async () => {
        try {
            setLoading(true);
            colSectionInstance.setTitle(title);
            colSectionInstance.setDescription(description);
            colSectionInstance.setLeftAnimation(leftAnimation);
            colSectionInstance.setRightAnimation(rightAnimation);
            colSectionInstance.setDirection(direction);
            await colSectionInstance.save();
            toast.success("Changes saved successfully!");
            await getData();
        } catch (error) {
            toast.error("Failed to save changes. Please try again.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <div className="container mt-4">
            <ToastContainer/>
            {defaultMessage &&
                <Alert variant="info" className="mt-3" dismissible>A default data has been loaded, Happy Building!</Alert>
            }
            <h3 className="mb-3">Column Section Builder</h3>
            <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Content</Accordion.Header>
                    <Accordion.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Enter section title"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Enter section description"
                                />
                            </Form.Group>

                           

                            <Form.Group className="mb-3">
                                <Form.Label>Media (Image/Video) <span className="text-danger">*</span></Form.Label>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    {imageUrl ? (
                                        <Card style={{ width: '200px', height: '120px' }}>
                                            {/(mp4|mov|webm|avi|mkv|m4v|ogg|wmv)$/i.test(imageUrl) ? (
                                                <video
                                                    src={imageUrl}
                                                    style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                                                    muted
                                                    preload="metadata"
                                                />
                                            ) : /(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|tiff|tif)$/i.test(imageUrl) ? (
                                                <Card.Img 
                                                    variant="top" 
                                                    src={imageUrl} 
                                                    style={{ objectFit: 'cover', height: '100%' }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = '/placeholder-image.jpg';
                                                    }}
                                                />
                                            ) : (
                                                <iframe
                                                    src={imageUrl}
                                                    style={{ height: '100%', width: '100%', border: 'none' }}
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                    allowFullScreen
                                                    loading="lazy"
                                                    referrerPolicy="strict-origin-when-cross-origin"
                                                    title="col-section-embed-preview"
                                                />
                                            )}
                                        </Card>
                                    ) : (
                                        <div style={{ width: '200px', height: '120px', border: '1px dashed #ccc' }} className="d-flex align-items-center justify-content-center">
                                            No media
                                        </div>
                                    )}
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        onClick={() => setIsMediaSelectorOpen(true)}
                                    >
                                        Select Media
                                    </Button>
                                </div>
                            </Form.Group>

                            <div className="mb-3">
                                <h5>Buttons</h5>
                                {buttons.map((btn) => (
                                    <div key={btn.id} className="mb-2 p-2 border rounded">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span>{btn.button.text}</span>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => removeButton(btn.id)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                <div>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Button Text <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            required
                                            type="text"
                                            value={newButton.text}
                                            onChange={(e) => setNewButton({ ...newButton, text: e.target.value })}
                                            placeholder="Button text"
                                            isInvalid={errors.newButton.text}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Button text is required
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Button Link <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            required
                                            type="text"
                                            value={newButton.link}
                                            onChange={(e) => setNewButton({ ...newButton, link: e.target.value })}
                                            placeholder="Button link"
                                            isInvalid={errors.newButton.link}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            Button link is required
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Button Type</Form.Label>
                                        <Form.Select
                                            value={newButton.buttonType}
                                            onChange={(e) => setNewButton({ ...newButton, buttonType: e.target.value })}
                                        >
                                            <option value="primary">Primary</option>
                                            <option value="secondary">Secondary</option>
                                        </Form.Select>
                                    </Form.Group>
                                    <Button
                                        variant="primary"
                                        onClick={addButton}
                                        disabled={!newButton.text || !newButton.link}
                                    >
                                        Add Button
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                    <Accordion.Header>Style Settings</Accordion.Header>
                    <Accordion.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Direction</Form.Label>
                            <Form.Select
                                value={direction}
                                onChange={(e) => setDirection(e.target.value)}
                            >
                                <option value="normal">Normal</option>
                                <option value="reversed">Reversed</option>
                            </Form.Select>
                        </Form.Group>
                        <div className="mb-3">
                            <label className="form-label">Background Color</label>
                            <div className="d-flex align-items-center">
                                <div
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.ColSection?.backgroundColor || '#ffffff',
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowBgColorPicker(!showBgColorPicker)}
                                />
                                <span>{currentStyle.ColSection?.backgroundColor || '#ffffff'}</span>
                            </div>
                            {showBgColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowBgColorPicker(false)}
                                    />
                                    <SketchPicker
                                        color={currentStyle.ColSection?.backgroundColor || '#ffffff'}
                                        onChange={handleBgColorChange}
                                    />
                                </div>
                            )}
                        </div>
                      

                        <div className="mb-3">
                            <label className="form-label">Title Color</label>
                            <div className="d-flex align-items-center">
                                <div
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.ColSectionTitle?.color || '#000000',
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowTitleColorPicker(!showTitleColorPicker)}
                                />
                                <span>{currentStyle.ColSectionTitle?.color || '#000000'}</span>
                            </div>
                            {showTitleColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowTitleColorPicker(false)}
                                    />
                                    <SketchPicker
                                        color={currentStyle.ColSectionTitle?.color || '#000000'}
                                        onChange={handleTitleColorChange}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Description Color</label>
                            <div className="d-flex align-items-center">
                                <div
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.ColSectionDescription?.color || '#666666',
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowDescriptionColorPicker(!showDescriptionColorPicker)}
                                />
                                <span>{currentStyle.ColSectionDescription?.color || '#666666'}</span>
                            </div>
                            {showDescriptionColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowDescriptionColorPicker(false)}
                                    />
                                    <SketchPicker
                                        color={currentStyle.ColSectionDescription?.color || '#666666'}
                                        onChange={handleDescriptionColorChange}
                                    />
                                </div>
                            )}
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                    <Accordion.Header>Animation</Accordion.Header>
                    <Accordion.Body>
                        <div className="row">
                            <div className="col-md-6">
                                <Card className="mb-4">
                                    <Card.Header className="bg-light">
                                        <h5 className="mb-0">Media (Left Column)</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-3">
                                            <Form.Label>Type</Form.Label>
                                            <Form.Select
                                                value={leftAnimation.animationType}
                                                onChange={(e) => {
                                                    const updated = { ...leftAnimation, animationType: e.target.value };
                                                    setLeftAnimation(updated);
                                                    colSectionInstance.setLeftAnimation(updated);
                                                }}
                                            >
                                                {availableAnimations.map((anim) => (
                                                    <option key={anim} value={anim}>{anim}</option>
                                                ))}
                                            </Form.Select>
                                        </div>
                                        <div className="mb-3">
                                            <Form.Label>Duration (ms)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={leftAnimation.animationDuration}
                                                onChange={(e) => {
                                                    const updated = { ...leftAnimation, animationDuration: e.target.value };
                                                    setLeftAnimation(updated);
                                                    colSectionInstance.setLeftAnimation(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="mb-0">
                                            <Form.Label>Delay (ms)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={leftAnimation.animationDelay}
                                                onChange={(e) => {
                                                    const updated = { ...leftAnimation, animationDelay: e.target.value };
                                                    setLeftAnimation(updated);
                                                    colSectionInstance.setLeftAnimation(updated);
                                                }}
                                            />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-6">
                                <Card className="mb-4">
                                    <Card.Header className="bg-light">
                                        <h5 className="mb-0">Content (Right Column)</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-3">
                                            <Form.Label>Type</Form.Label>
                                            <Form.Select
                                                value={rightAnimation.animationType}
                                                onChange={(e) => {
                                                    const updated = { ...rightAnimation, animationType: e.target.value };
                                                    setRightAnimation(updated);
                                                    colSectionInstance.setRightAnimation(updated);
                                                }}
                                            >
                                                {availableAnimations.map((anim) => (
                                                    <option key={anim} value={anim}>{anim}</option>
                                                ))}
                                            </Form.Select>
                                        </div>
                                        <div className="mb-3">
                                            <Form.Label>Duration (ms)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={rightAnimation.animationDuration}
                                                onChange={(e) => {
                                                    const updated = { ...rightAnimation, animationDuration: e.target.value };
                                                    setRightAnimation(updated);
                                                    colSectionInstance.setRightAnimation(updated);
                                                }}
                                            />
                                        </div>
                                        <div className="mb-0">
                                            <Form.Label>Delay (ms)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={rightAnimation.animationDelay}
                                                onChange={(e) => {
                                                    const updated = { ...rightAnimation, animationDelay: e.target.value };
                                                    setRightAnimation(updated);
                                                    colSectionInstance.setRightAnimation(updated);
                                                }}
                                            />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <div className="SaveChangesContainer d-flex justify-content-end mt-3">
                <ToastContainer />
                <Button 
                    variant="primary" 
                    onClick={handleSaveChanges}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Saving...
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </div>

            <MediaSelector
                isOpen={isMediaSelectorOpen}
                onClose={() => setIsMediaSelectorOpen(false)}
                onSelect={handleMediaSelect}
                isMultiple={false}
            />
        </div>
    );
};

export default ColSectionBuilder;
